import { Refund } from '../Refunds/types';

export enum PaymentAgreementType {
  Tuition = 'tuition',
  Tariff = 'tariff',
}

/**
 * Descuento aplicado a un CONCEPTO (el acuerdo), no a una cuota puntual.
 * `percentage` es null en montos fijos y subsidios: un monto fijo no se expresa
 * como porcentaje.
 */
export interface ConceptDiscount {
  code: string;
  name: string;
  percentage: number | null;
  amount: number;
}

/**
 * Desglose de un monto (pagado o pendiente) en los cuatro conceptos que
 * componen una cuota, en orden de imputación (ver DebtImputationService).
 */
export interface ConceptAmounts {
  capital: number;
  currentInterest: number;
  lateInterest: number;
  collectionFees: number;
}

/**
 * Unified row consumed by the Cuenta corriente table. Pending refunds are
 * rendered inline alongside debts (XD HU-15415): debt rows render as before,
 * refund rows show the refund concept/date in the Cargo column, the gross
 * amount in Abono, the available balance highlighted in Deuda, and an
 * "Aplicar devolución" button in Acción. Most other cells render N/A for
 * refunds.
 */
export type DebtTableRow =
  | ({ __kind: 'debt' } & Debt)
  | { __kind: 'refund'; id: string; refund: Refund };

export interface Debt {
  //identificador de la deuda
  id: number;
  //Corresponde a una descripción, cada deuda puede definir su concepto en su Mapper
  concept: string;
  //Tipo de acuerdo de momento tenemos arancel y matricula, pero luego vinen las repactaciones
  agreementType: PaymentAgreementType;
  //Nº de cuota: ejemplo 1/7(uno de siete cuotas)
  numberOfInstallment: string;
  //Fecha de vencimiento de la cuota
  expirationDate: string;
  //dias de atraso(días de mora)
  daysLate: number;
  //Monto inicial: el total de la cuota (equivalente a capital + interés corriente)
  initialAmount: number;
  //Capital de la cuota (desglose de los cuatro conceptos, ver DebtBreakdownService)
  capitalAmount: number;
  //Bruto del concepto antes de descuentos
  amountGross: number;
  //Desglose del CONCEPTO: un arancel en 10 cuotas repite el mismo desglose
  discounts: ConceptDiscount[];
  interestRate: number;
  interestAmount: number;
  lateInterestAmount: number;
  totalAmount: number;
  paidAmount: number;
  outstandingDebt: number;
  studyPlanId: number | null;
  studyPlanName: string | null;
  year: number;
  period: number | null;
  periodId: number | null;
  periodName: string | null;
  idToDisplay: string | null;
  collectionFees: number;
  paidBreakdown: ConceptAmounts;
  outstandingBreakdown: ConceptAmounts;
  payableExternalDebt: PayableExternalDebt | null;
}

export interface PayableExternalDebt {
  provider: string;
  /**
   * Si esta fila puede entrar en un link que cobra varias cuotas. Lo resuelve
   * el backend combinando el flag del tenant con la capability de la pasarela
   * (`supportsMultiDebtPay` del portal): el front no vuelve a decidirlo.
   */
  supportsMultiDebtPay: boolean;
}

export interface PaymentLinkResponse {
  url: string;
  qrCodeDataUrl: string;
}

export enum StudentFinancialStatusCode {
  Active = 'active',
  FinancialBlock = 'financialBlock',
  TemporaryUnblock = 'temporaryUnblock',
}

export interface StudentFinancialStatus {
  status: StudentFinancialStatusCode;
  temporaryUnblockUntil: string | null;
}

export interface DebtResponse {
  studentFinancialStatus: StudentFinancialStatus;
  // Flag pelado del tenant: sólo dice si la funcionalidad de link de pago
  // (de UNA cuota) está habilitada.
  allowExternalDebtPaymentLink: boolean;
  /**
   * A diferencia de `allowExternalDebtPaymentLink`, esto ya combina TRES
   * cosas resueltas por el backend: el flag de multi del tenant, la
   * capability de la pasarela confirmada por el portal, y que al menos una
   * fila de ESTE estudiante haya quedado marcada como pagable por esa
   * pasarela. `true` no garantiza que CUALQUIER selección sea combinable —
   * eso lo sigue decidiendo fila por fila `Debt.payableExternalDebt`.
   */
  allowMultiDebtPaymentLink: boolean;
  debts: Debt[];
}

// HU #15319 — available filter statuses. Mirrors the API enum
// `CurrentAccountDebtFilterStatus` so values are serialized as-is to the
// `status` query param.
export enum DebtStatusFilter {
  Pending = 'pending',
  Overdue = 'overdue',
  Paid = 'paid',
}

// Draft/applied filter state for the DebtFilters component.
// `null` means "no filter applied on this dimension".
export interface DebtFiltersState {
  year: number | null;
  period: number | null;
  studyPlanId: number | null;
  status: DebtStatusFilter | null;
}

export const EMPTY_DEBT_FILTERS: DebtFiltersState = {
  year: null,
  period: null,
  studyPlanId: null,
  status: null,
};

// HU #15322 — charge item from GET /charges/items
export interface ChargeItem {
  id: number;
  name: string;
  costCenter: string | null;
  currency: string | null;
  amount: number | null;
}

// HU #15322 — body for POST /students/:studentId/charges
export interface CreateChargeBody {
  studyPlanId: number;
  periodId: number;
  chargeItemId?: number | null;
  concept?: string;
  expirationDate: string;
  costCenter?: string;
  currency?: string;
  amount?: number;
}
